import React from "react";
import * as Mui from "@mui/material";
import {
  designColors,
  designFontFamily,
} from "services/constants/designTokens";

const getRawApprovalAuditTrail = (record) => {
  const trail =
    record?.edit_audit_trail ||
    record?.editAuditTrail ||
    record?.audit_trail ||
    record?.auditTrail ||
    [];

  if (!Array.isArray(trail)) return [];
  return trail;
};

const parseAuditMessage = (message) => {
  if (!message || typeof message !== "string") return {};

  const match = message.match(
    /^Edited by\s+(.+?)\s+on\s+(.+?)(?:\s+(?:\u2014|\u2013|-)\s+(.+))?$/i
  );

  if (!match) return { message };

  return {
    actor: match[1],
    editedAt: match[2],
    changedFields: match[3],
    message,
  };
};

export const getApprovalAuditTrailEntries = (record) =>
  getRawApprovalAuditTrail(record)
    .map((entry) => {
      if (typeof entry === "string") return parseAuditMessage(entry);

      const message =
        entry?.message || entry?.audit_message || entry?.description || "";
      const parsed = parseAuditMessage(message);

      return {
        ...parsed,
        actor:
          parsed.actor ||
          entry?.actor ||
          entry?.actor_name ||
          entry?.edited_by ||
          entry?.updated_by ||
          entry?.user,
        editedAt:
          parsed.editedAt ||
          entry?.edited_at ||
          entry?.updated_at ||
          entry?.created_at ||
          entry?.timestamp,
        changedFields:
          parsed.changedFields ||
          entry?.changed_fields ||
          entry?.fields_changed ||
          entry?.field_names,
        message,
      };
    })
    .filter((entry) => entry?.message || entry?.actor || entry?.changedFields);

export const getApprovalAuditTrail = (record) => {
  return getApprovalAuditTrailEntries(record)
    .map((entry) => entry.message)
    .filter(Boolean);
};

export const ApprovalAuditTrail = ({ record, messages, compact = false }) => {
  const auditEntries = messages
    ? messages.map((message) => parseAuditMessage(message)).filter(Boolean)
    : getApprovalAuditTrailEntries(record);

  if (!auditEntries.length) return null;

  return (
    <Mui.Stack
      spacing={0}
      sx={{
        mt: compact ? 0 : 1.5,
        fontFamily: designFontFamily,
      }}
    >
      {auditEntries.map((entry, index) => (
        <Mui.Box
          key={`${entry.message || entry.actor || "edit"}-${index}`}
          sx={{
            position: "relative",
            pl: "20px",
            mb: 0,
            color: designColors.slate700,
            fontSize: 13,
            lineHeight: 1.5,
            fontFamily: designFontFamily,
          }}
        >
          <Mui.Box
            sx={{
              width: 10,
              height: 10,
              borderRadius: "50%",
              backgroundColor: "#C19A6B",
              position: "absolute",
              left: 0,
              top: 6,
            }}
          />
          <Mui.Typography
            component="span"
            sx={{
              display: "flex",
              flexWrap: "wrap",
              gap: "6px",
              mt: "2px",
              fontSize: 14,
              color: designColors.slate900,
              lineHeight: 1.5,
              fontFamily: designFontFamily,
            }}
          >
            <Mui.Box component="span" sx={{ fontWeight: 500 }}>
              Edited by
            </Mui.Box>
            <Mui.Box component="span">{entry.actor || "-"}</Mui.Box>
            <Mui.Box component="span" sx={{ fontWeight: 600 }}>
              on
            </Mui.Box>
            <Mui.Box component="span">{entry.editedAt || "-"}</Mui.Box>
            {entry.changedFields ? (
              <Mui.Box component="span" sx={{ color: designColors.slate700 }}>
                - {entry.changedFields}
              </Mui.Box>
            ) : null}
          </Mui.Typography>
        </Mui.Box>
      ))}
    </Mui.Stack>
  );
};

export default ApprovalAuditTrail;
